import { parseActionGraph, type ActionGraph } from "../actions.ts";
import type { Condition } from "../conditions.ts";
import type { ProjectView } from "../entities.ts";
import { issue } from "./helpers.ts";
import type { ValidationReport } from "./types.ts";

export const computeUnused = (view: ProjectView, report: ValidationReport): void => {
	const referencedItems = new Set<string>();
	const referencedAttacks = new Set<string>();
	const referencedCurrencies = new Set<string>();
	const referencedStats = new Set<string>();
	const referencedRecipes = new Set<string>();

	const scanCondition = (cond: Condition): void => {
		if (cond.type === "HasItem") referencedItems.add(cond.itemId);
		else if (cond.type === "HasCurrency")
			referencedCurrencies.add(cond.currencyId);
		else if (cond.type === "HasStat") referencedStats.add(cond.statKey);
		else if (cond.type === "IsOnZone") referencedItems.add(cond.zoneItemId);
	};

	const scanGraph = (graph: ActionGraph | null | undefined): void => {
		if (!graph) return;
		for (const node of Object.values(graph.nodes)) {
			const a = node as unknown as Record<string, unknown>;
			const t = node.type as string;
			if (t === "ModifyItem" && typeof a.itemId === "string") {
				referencedItems.add(a.itemId);
			} else if (t === "AddRandomItem") {
				const entries = (a.entries as Array<Record<string, unknown>>) ?? [];
				for (const e of entries) {
					if (typeof e.itemId === "string") referencedItems.add(e.itemId);
				}
			} else if (t === "ModifyCurrency" && typeof a.currencyId === "string") {
				referencedCurrencies.add(a.currencyId);
			} else if (t === "Transform") {
				const recipes = (a.recipes as string[]) ?? [];
				for (const r of recipes) referencedRecipes.add(r);
			} else if (
				(t === "ModifyStat") &&
				typeof a.statKey === "string"
			) {
				referencedStats.add(a.statKey);
			} else if ((t === "SetGoal" || t === "ChangeBehavior") && typeof a.targetItemId === "string") {
				referencedItems.add(a.targetItemId);
			} else if (t === "Spawn") {
				const list = (a.contentList as string[]) ?? [];
				for (const c of list) referencedItems.add(c);
			} else if (t === "Branch" || t === "WaitUntil") {
				const conditions = a.conditions as Condition[] | undefined;
				if (Array.isArray(conditions)) conditions.forEach(scanCondition);
			}
		}
	};

	for (const item of Object.values(view.items)) {
		if (item.attacks) for (const a of item.attacks) referencedAttacks.add(a);
		scanGraph(parseActionGraph(item.graph));
		if (item.stats)
			for (const k of Object.keys(item.stats)) referencedStats.add(k);
	}
	for (const attack of Object.values(view.attacks)) {
		if (attack.costStat) referencedStats.add(attack.costStat);
		for (const d of attack.damage) referencedStats.add(d.statKey);
	}
	for (const recipe of Object.values(view.recipes)) {
		for (const input of recipe.inputs ?? []) {
			if (input.type === "item") referencedItems.add(input.itemId);
			else referencedCurrencies.add(input.currencyId);
		}
		for (const output of recipe.outputs) {
			if (output.type === "item") referencedItems.add(output.itemId);
			else referencedCurrencies.add(output.currencyId);
		}
	}
	for (const quest of Object.values(view.quests)) {
		const objectives = [
			...(Array.isArray(quest.winObjectives) ? quest.winObjectives : []),
			...(Array.isArray(quest.loseObjectives) ? quest.loseObjectives : []),
		];
		for (const objective of objectives) {
			const tid = objective?.itemId;
			if (typeof tid !== "string" || !tid) continue;
			if (objective.action === "Craft") referencedRecipes.add(tid);
			else if (objective.action === "ReachStat") referencedStats.add(tid);
			else referencedItems.add(tid);
		}
		for (const r of quest.rewards) {
			if (r.type === "item") referencedItems.add(r.itemId);
			else if (r.type === "currency") referencedCurrencies.add(r.currencyId);
			else if (r.type === "stat") referencedStats.add(r.statKey);
		}
	}
	for (const stat of Object.values(view.stats)) {
		if (stat.progression) {
			referencedStats.add(stat.progression.targetStat);
			for (const th of stat.progression.thresholds) {
				for (const k of Object.keys(th.baseStats)) referencedStats.add(k);
			}
		}
	}

	for (const id of Object.keys(view.items)) {
		const item = view.items[id];
		if (!item) continue;
		const hasModel =
			Array.isArray(item.modelPaths) && item.modelPaths.length > 0;
		const graph = parseActionGraph(item.graph);
		const hasGraph = !!graph && Object.keys(graph.nodes).length > 0;
		const isReferenced = referencedItems.has(id);

		if (hasModel && hasGraph) continue;
		if (isReferenced) continue;

		if (hasGraph && !hasModel) {
			report.warnings.push(
				issue(
					"warning",
					"items",
					id,
					[],
					`Item '${id}' has a graph but no 3D model`,
					"item_unmapped",
				),
			);
			continue;
		}

		report.warnings.push(
			issue(
				"warning",
				"items",
				id,
				[],
				`Unused item '${id}' — not referenced anywhere`,
				"unused",
			),
		);
	}
	for (const id of Object.keys(view.attacks)) {
		const a = view.attacks[id];
		if (!a) continue;
		if (!referencedAttacks.has(id)) {
			report.warnings.push(
				issue(
					"warning",
					"attacks",
					id,
					[],
					`Unused attack '${id}' — not referenced by any item`,
					"unused",
				),
			);
		}
	}
	for (const id of Object.keys(view.recipes)) {
		const recipe = view.recipes[id];
		if (!recipe) continue;
		const hasDeveloperProduct =
			recipe.developerProductId !== null &&
			recipe.developerProductId !== undefined &&
			recipe.developerProductId !== "";
		if (hasDeveloperProduct) continue;
		if (!referencedRecipes.has(id)) {
			report.warnings.push(
				issue(
					"warning",
					"recipes",
					id,
					[],
					`Unused recipe '${id}' — not referenced by any Transform node or quest`,
					"unused",
				),
			);
		}
	}
	for (const id of Object.keys(view.currencies)) {
		if (!referencedCurrencies.has(id)) {
			report.warnings.push(
				issue(
					"warning",
					"currencies",
					id,
					[],
					`Unused currency '${id}'`,
					"unused",
				),
			);
		}
	}
	for (const id of Object.keys(view.stats)) {
		if (!referencedStats.has(id)) {
			report.warnings.push(
				issue(
					"warning",
					"stats",
					id,
					[],
					`Unused stat '${id}'`,
					"unused",
				),
			);
		}
	}
};
